/**
 * Variant Card Scroll Reveal
 * 
 * Simple scroll-based reveal: when user scrolls to 75% of page,
 * add active class to cards with data-page attributes
 */

(function() {
  'use strict';

  let isRevealing = false;
  let scrollHandler = null;
  let isInitialized = false;
  const MIN_VISIBLE_CARDS = 9;
  // Reveal much earlier for better perceived performance
  const REVEAL_NEAR_BOTTOM_PX = 2500; // Increased from 1400px for earlier reveal
  const SCROLL_THROTTLE_MS = 30; // Reduced from 50ms for faster response

  function getAllCards(grid) {
    // Get all cards with ref="cards[]" attribute - brackets don't work in CSS selectors
    let allCards = Array.from(grid.querySelectorAll('[ref]')).filter(el => el.getAttribute('ref') === 'cards[]');
    if (allCards.length === 0) {
      allCards = Array.from(grid.children).filter(el => el.getAttribute('ref') === 'cards[]');
    }
    return allCards;
  }

  function throttle(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  function checkScrollPosition() {
    if (isRevealing) {
      return;
    }

    // Find the product grid
    const grid = document.querySelector('.product-grid');
    if (!grid) {
      return;
    }

    // Count currently visible active cards
    const allCards = getAllCards(grid);
    const activeCards = allCards.filter(card => 
      card.classList.contains('active') && !card.classList.contains('filter-hidden')
    );
    
    // If we have 9 or more cards visible, check if we should load more
    if (activeCards.length >= MIN_VISIBLE_CARDS) {
      // Batch all scroll/geometry reads together to avoid forced reflows
      const windowHeight = window.innerHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = (scrollTop + windowHeight) / documentHeight;
      const distanceToBottom = documentHeight - (scrollTop + windowHeight);

      // Check if last visible card is in viewport or near bottom
      if (activeCards.length > 0) {
        const lastCard = activeCards[activeCards.length - 1];
        if (lastCard instanceof HTMLElement) {
          const lastCardRect = lastCard.getBoundingClientRect();
          // If last card is visible or near viewport, load next batch
          if (lastCardRect.top < windowHeight + 500 || distanceToBottom <= REVEAL_NEAR_BOTTOM_PX || scrollPercentage >= 0.5) {
            revealNextBatch();
            return;
          }
        }
      }
    } else {
      // If we have fewer than 9 cards, ensure we have at least 9
      revealNextBatch();
    }
  }

  function revealNextBatch() {
    if (isRevealing) return;
    isRevealing = true;

    // Find the product grid
    const grid = document.querySelector('.product-grid');
    if (!grid) {
      isRevealing = false;
      return;
    }

    // Get all cards
    const allCards = getAllCards(grid);
    
    // Count currently active cards (not filter-hidden)
    const activeCards = allCards.filter(card => 
      card.classList.contains('active') && !card.classList.contains('filter-hidden')
    );
    
    // If we have 9 or more active cards, load the next 9
    // Otherwise, ensure we have at least 9 visible
    const targetCount = activeCards.length >= MIN_VISIBLE_CARDS ? activeCards.length + MIN_VISIBLE_CARDS : MIN_VISIBLE_CARDS;
    
    // Find all eligible hidden cards (not filter-hidden)
    const eligibleHiddenCards = allCards.filter(card => {
      const isHidden = card.classList.contains('product-grid__item--hidden');
      const isFilterHidden = card.classList.contains('filter-hidden');
      return isHidden && !isFilterHidden;
    });
    
    if (eligibleHiddenCards.length === 0) {
      isRevealing = false;
      return;
    }
    
    // Calculate how many cards to reveal (up to 9, or until we reach target count)
    const cardsToReveal = Math.min(MIN_VISIBLE_CARDS, eligibleHiddenCards.length, targetCount - activeCards.length);
    
    if (cardsToReveal <= 0) {
      isRevealing = false;
      return;
    }
    
    // Reveal the next batch of cards (up to 9)
    const cardsToShow = eligibleHiddenCards.slice(0, cardsToReveal);
    
    // Add active class and remove pagination-hidden class
    // IMPORTANT: Do NOT remove filter-hidden class - that's controlled by the filter system
    cardsToShow.forEach((card) => {
      if (card instanceof HTMLElement) {
        // Skip cards that are filter-hidden - they should stay hidden
        if (card.classList.contains('filter-hidden')) {
          return;
        }
        
        card.classList.remove('product-grid__item--hidden');
        // DO NOT remove filter-hidden - that's controlled by the color filter
        card.classList.add('active');
        // Only set display block if NOT filter-hidden
        card.style.setProperty('display', 'block', 'important');
      }
    });

    // Update product count to reflect total visible cards
    try {
      const visibleCards = document.querySelectorAll('.product-grid__item[data-variant-color].active:not(.filter-hidden)');
      const count = visibleCards.length;
      const countEls = document.querySelectorAll('.products-count-wrapper span');
      countEls.forEach((el) => {
        if (el instanceof HTMLElement) {
          const txt = (el.textContent || '').trim();
          if (/\d/.test(txt)) {
            el.textContent = txt.replace(/\d[\d,]*/, String(count));
          } else {
            el.textContent = String(count);
          }
        }
      });
    } catch (e) {
      // Error updating product count
    }

    // Initialize variant pickers
    try {
      const globalWindow = /** @type {any} */ (window);
      if (globalWindow.setupVariantCardPickers) {
        globalWindow.setupVariantCardPickers();
      }
      
      // Initialize native swatch carousels for newly revealed cards
      if (globalWindow.SuniceSwatchCarousel && typeof globalWindow.SuniceSwatchCarousel.init === 'function') {
        hiddenCards.forEach((card) => {
          if (card instanceof HTMLElement) globalWindow.SuniceSwatchCarousel.init(card);
        });
      }
    } catch (e) {
      // Error initializing variant card pickers
    }

    // Reset flag quickly so we can reveal additional pages sooner (more responsive).
    // Also: if the user is still near the bottom after DOM height changes, chain-reveal
    // more pages so we don't "skip" cards when scrolling fast.
    setTimeout(() => {
      isRevealing = false;

      // If still near bottom, reveal a few more pages immediately (safety-capped)
      try {
        // Batch all scroll/geometry reads together
        const windowHeight = window.innerHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const documentHeight = document.documentElement.scrollHeight;
        const distanceToBottom = documentHeight - (scrollTop + windowHeight);

        if (distanceToBottom <= REVEAL_NEAR_BOTTOM_PX) {
          let chained = 0;
          const MAX_CHAINED_REVEALS = 3;
          while (!isRevealing && chained < MAX_CHAINED_REVEALS) {
            // Attempt to reveal next batch; if nothing revealed, it will reset isRevealing and return.
            const beforeHiddenEligible = document.querySelectorAll('.product-grid__item.product-grid__item--hidden:not(.filter-hidden)').length;
            revealNextBatch();
            const afterHiddenEligible = document.querySelectorAll('.product-grid__item.product-grid__item--hidden:not(.filter-hidden)').length;
            chained++;
            // Stop chaining if no progress was made
            if (afterHiddenEligible >= beforeHiddenEligible) break;
          }
          return;
        }
      } catch (e) {
        // Chain reveal failed
      }

      checkScrollPosition(); // Check again in case we need to reveal more
    }, 40);
  }

  function ensureMinVisible(grid) {
    // Reveal enough eligible cards immediately so the page isn't "stuck" when short / filtered.
    const allCards = getAllCards(grid);
    const eligibleCards = allCards.filter(card => !card.classList.contains('filter-hidden'));
    const eligibleVisible = eligibleCards.filter(card => card.classList.contains('active') && !card.classList.contains('product-grid__item--hidden'));

    // If a color filter is active, reveal ALL eligible (matching) cards immediately.
    // This avoids the "I selected Taupe and need to scroll to see the rest" issue.
    try {
      const urlParams = new URLSearchParams(window.location.search);
      let hasActiveColorFilter = false;
      for (const [key, value] of urlParams.entries()) {
        if (!value) continue;
        const k = String(key).toLowerCase();
        if (k.startsWith('filter.') && (k.includes('color') || k.includes('colour') || k.includes('s_d_variant_color_filter'))) {
          hasActiveColorFilter = true;
          break;
        }
      }

      if (hasActiveColorFilter) {
        eligibleCards.forEach(card => {
          if (!(card instanceof HTMLElement)) return;
          card.classList.remove('product-grid__item--hidden');
          card.classList.add('active');
          card.style.setProperty('display', 'block', 'important');
        });
        return;
      }
    } catch (e) {
      // Filter detection failed
    }

    // If total eligible cards <= 24, reveal them all immediately.
    if (eligibleCards.length > 0 && eligibleCards.length <= MIN_VISIBLE_CARDS) {
      eligibleCards.forEach(card => {
        if (!(card instanceof HTMLElement)) return;
        card.classList.remove('product-grid__item--hidden');
        card.classList.add('active');
        card.style.setProperty('display', 'block', 'important');
      });
      return;
    }

    // If we have fewer than 24 eligible visible cards, reveal pages until we reach 24 (or run out).
    if (eligibleVisible.length >= MIN_VISIBLE_CARDS) return;

    // Group eligible hidden cards by page
    const byPage = new Map();
    eligibleCards.forEach(card => {
      const page = parseInt(card.getAttribute('data-page') || '0', 10);
      if (!page) return;
      if (!card.classList.contains('product-grid__item--hidden')) return;
      if (!byPage.has(page)) byPage.set(page, []);
      byPage.get(page).push(card);
    });

    const pages = Array.from(byPage.keys()).sort((a, b) => a - b);
    let visibleCount = eligibleVisible.length;
    const revealedNow = [];

    for (const page of pages) {
      if (visibleCount >= MIN_VISIBLE_CARDS) break;
      const cardsOnPage = byPage.get(page) || [];
      cardsOnPage.forEach(card => {
        if (!(card instanceof HTMLElement)) return;
        card.classList.remove('product-grid__item--hidden');
        card.classList.add('active');
        card.style.setProperty('display', 'block', 'important');
        revealedNow.push(card);
        visibleCount++;
      });
    }

    if (revealedNow.length > 0) {
      try {
        const globalWindow = /** @type {any} */ (window);
        if (globalWindow.setupVariantCardPickers) {
          globalWindow.setupVariantCardPickers();
        }
        if (globalWindow.SuniceColorSwiperInstance) {
          revealedNow.forEach((card) => {
            const swiperContainers = card.querySelectorAll('.color-swiper-container');
            swiperContainers.forEach((container) => {
              if (container instanceof HTMLElement &&
                  container.dataset.suniceColorSwiperInitialized !== 'true') {
                globalWindow.SuniceColorSwiperInstance.initializeColorSwiper(container);
              }
            });
          });
        }
      } catch (e) {
        // Init failed
      }
    }
  }

  function init(retryCount = 0) {
    const MAX_RETRIES = 3;
    
    // Check if variant card scroll reveal is enabled in settings
    if (!window.variantCardScrollRevealEnabled) {
      return;
    }
    
    // Reset initialization state on first call (not retries) to allow re-detection after filtering
    if (retryCount === 0 && isInitialized) {
      // Don't reset scrollHandler - keep it attached
    }
    
    // Check if we're on a collection page with variant cards
    const grid = document.querySelector('.product-grid');
    if (!grid) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => init(retryCount + 1), 200);
      }
      return;
    }

    // Check if variant cards exist - multiple detection methods
    let isVariantCards = false;
    
    // CRITICAL: Only activate for variant cards, NOT regular product cards
    // Check for data-variant-color on grid items (most reliable for variant listing mode)
    // Use querySelectorAll which works even when grid is hidden or items are filtered
    // Also search in the entire document in case grid was replaced after filtering
    // IMPORTANT: querySelectorAll finds elements even if they have display:none or filter-hidden class
    const variantGridItems = grid.querySelectorAll('.product-grid__item[data-variant-color]');
    const variantItemsInDoc = document.querySelectorAll('.product-grid__item[data-variant-color]');
    // Use the larger count (items might be in grid or document after AJAX updates)
    // Even if items are hidden, they're still in the DOM and should be found
    const totalVariantItems = Math.max(variantGridItems.length, variantItemsInDoc.length);
    
    const gridIsHidden = window.getComputedStyle(grid).display === 'none';
    
    if (totalVariantItems > 0) {
      isVariantCards = true;
    } else {
      // Debug: check if grid has any children at all
      const gridChildren = Array.from(grid.children);
      const gridChildrenWithData = gridChildren.filter(child => child.hasAttribute && child.hasAttribute('data-variant-color'));
      
      // Also check direct children if querySelectorAll didn't work (shouldn't be needed, but safety check)
      if (gridChildrenWithData.length > 0) {
        isVariantCards = true;
      }
    }
    
    // If still not detected and we have retries left, wait a bit and try again
    // This handles cases where items are being added dynamically after filtering or AJAX updates
    const hasGridChildren = grid.children.length > 0;
    const hasVariantItems = variantItemsInDoc.length > 0;
    
    if (!isVariantCards && retryCount < MAX_RETRIES && !hasGridChildren && !hasVariantItems) {
      setTimeout(() => init(retryCount + 1), 200);
      return;
    }
    
    // Find first card - get all elements with ref attribute and filter (square brackets don't work in CSS selectors)
    const allRefs = Array.from(grid.querySelectorAll('[ref]'));
    const firstCard = allRefs.find(el => el.getAttribute('ref') === 'cards[]') || 
                      allRefs.find(el => el.getAttribute('ref') === 'cards []') ||
                      Array.from(grid.children).find(el => el.getAttribute('ref') === 'cards[]');
    
    // Also check for data-variant-id on product card element
    if (!isVariantCards && firstCard) {
      const productCardElement = firstCard.querySelector('.sunice-product-card');
      if (productCardElement && productCardElement.hasAttribute('data-variant-id')) {
        isVariantCards = true;
      }
      
      // Also check for variant picker element
      if (!isVariantCards && firstCard.querySelector('.sunice-variant-picker')) {
        isVariantCards = true;
      }
    }
    
    // Fallback: Check hidden cards for variant card indicators (only if first card check failed)
    if (!isVariantCards) {
      const hiddenCards = grid.querySelectorAll('.product-grid__item--hidden, .product-grid__item.filter-hidden');
      for (let i = 0; i < Math.min(3, hiddenCards.length); i++) {
        const hiddenCard = hiddenCards[i];
        // Check for data-variant-color first
        if (hiddenCard.hasAttribute('data-variant-color')) {
          isVariantCards = true;
          break;
        }
        // Then check for data-variant-id on product card
        const productCard = hiddenCard?.querySelector('.sunice-product-card');
        if (productCard && productCard.hasAttribute('data-variant-id')) {
          isVariantCards = true;
          break;
        }
      }
    }

    if (!isVariantCards) {
      return;
    }

    // Set up scroll listener - this fires every time user scrolls
    // Only add listener once (idempotent)
    if (!scrollHandler) {
      scrollHandler = throttle(() => {
        checkScrollPosition();
      }, SCROLL_THROTTLE_MS);
      
      // Add scroll event listener
      window.addEventListener('scroll', scrollHandler, { passive: true });
    }
    
    isInitialized = true;
    
    // If we're short (esp. after filters), reveal enough cards immediately
    ensureMinVisible(grid);

    // Check on initial load in case page is already scrolled
    checkScrollPosition();
  }

  // Expose init function globally for re-initialization after AJAX updates
  window.VariantCardScrollReveal = {
    init: init
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
    });
  } else {
    init();
  }
  
  // Also listen for facets:update to re-initialize after filtering
  document.addEventListener('facets:update', () => {
    setTimeout(() => {
      init();
    }, 200);
  });
})();
